<template>
  <div>
    <v-row justify="space-between" v-if="search">
      <v-col cols="12" xl="3" lg="3" md="3" sm="4" xs="12">
        <SearchText
          :text="'Search by name'"
          :value="'text'"
          @searchTextChanged="searchTextChanged"
          @keypress.enter.native.prevent="triggerSearch"
          @triggerSearch="triggerSearch"
          @triggerClear="triggerClear"
        ></SearchText>
      </v-col>
      <v-col cols="auto" class="d-flex align-center">
        <v-btn
          v-if="role === 'super-admin'"
          @click="downloadEquipment"
          outlined
          class="black--text font-weight-bold mb-2"
          rounded
          large
        >
          <v-icon color="black" small class="mr-2">
            $vuetify.icons.plus
          </v-icon>
          Download
        </v-btn>
        <v-btn
          @click="triggerBulkEdit"
          outlined
          class="white--text font-weight-bold mb-2 black ml-2"
          rounded
          large
        >
          Bulk Edit
        </v-btn>
      </v-col>
    </v-row>

    <!-- Bulk Edit Dialog -->
    <v-dialog v-model="dialogBulkEdit" max-width="500" persistent>
      <v-card>
        <v-card-title class="pb-5">
          <v-row>
            <v-col cols="12" class="justify-start d-flex">
              Bulk Edit
            </v-col>
          </v-row>
          <v-btn icon @click="dialogBulkEdit = false" left>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="mb-0 pb-0">
          <label>Equipment to copy from</label>
          <p class="label-info">
            Select the equipment you want to use as protorype
          </p>
          <input-autocomplete-search
            v-model="bulkEdit.equipmentPrototype"
            :item-value="'id'"
            :item-text="'name'"
            :url="'/api/v1/equipment/by-name?name='"
            label="Equipment to copy from"
            prepend-inner-icon="mdi-magnify"
            clearable
            return-object
            outlined
          ></input-autocomplete-search>
        </v-card-text>
        <v-card-text>
          <v-checkbox v-model="bulkEdit.logo" label="Same Logo" class="mt-0 pt-0" />
        </v-card-text>
        <v-card-text>
          <v-checkbox
            v-model="bulkEdit.publicContact"
            label="Same Public Contact"
            class="mt-0 pt-0"
          />
        </v-card-text>
        <v-card-actions class="pb-5">
          <v-row>
            <v-col cols="6">
              <v-btn
                block
                large
                class="rounded-lg black--text  font-weight-bold"
                outlined
                color="black"
                @click="cancelBulkEdit"
                >{{ $t('cancel') }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                large
                class="rounded-lg white--text  font-weight-bold"
                color="neutral"
                block
                @click="submitBulkEdit"
                >{{ $t('submit') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Dialog -->
    <v-dialog v-if="itemToBeDeleted !== null" v-model="deleteDialog" max-width="500">
      <v-card class="py-2 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title> Delete {{ itemToBeDeleted.name }} </v-card-title>
        <v-card-text>
          {{ $t('delete.confirm') }}
        </v-card-text>
        <v-card-actions>
          <v-row style="width: 100%">
            <v-col cols="6">
              <v-btn
                block
                depressed
                rounded
                large
                class="neutral800--text font-weight-bold"
                color="neutral"
                outlined
                @click="deleteDialog = false"
                >{{ $t('cancel') }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                block
                depressed
                rounded
                large
                class="white--text font-weight-bold"
                color="red darken-1"
                @click="deleteEquipment(itemToBeDeleted.id)"
              >
                {{ $t('delete.message') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Copy Dialog -->
    <v-dialog v-if="itemForCopy !== null" v-model="copyDialog" max-width="500">
      <v-card class="py-2 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title class="px-0 py-0">
          <v-row class="mx-0 px-2" align="center">
            <v-col cols="11" class="text-center d-flex justify-center align-center">
              <img id="logo-image" :src="require('@/assets/logo-purple.svg')" />
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider class="my-1 my-sm-6 my-lg-6 my-xl-6 my-md-6"></v-divider>
        <v-card-text class="text-center">
          <img id="logo-image" :src="require('@/assets/svg/copy.svg')" />
          <h3 class="pb-2">Copied {{ itemForCopy.name }}</h3>
          <p>
            Please click on the button to access the copied version.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-row style="width: 100%" class="text-center">
            <v-col cols="12">
              <v-btn
                v-if="newDuplicateItem"
                depressed
                rounded
                large
                class="white--text font-weight-bold"
                color="black"
                :to="{ name: 'OverviewEquipment', params: { id: newDuplicateItem.id } }"
                >Open copied Equipment
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      class="mt-5"
      :items="items"
      :options.sync="options"
      :server-items-length="totalItems"
      id="equipment-values"
      :single-select="false"
      v-model="equipments"
      show-select
    >
      <template v-slot:item.actions="{ item }">
        <v-layout>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                :to="{
                  name: 'EditEquipment',
                  params: {
                    id: item.id,
                    publicStatus: getPublicStatus(item.published),
                  },
                  query: {
                    fromTable: true,
                    ...getParams(),
                  },
                }"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-pencil-outline
                </v-icon>
              </v-btn>
            </template>
            {{ $t('edit') }}
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                :to="{
                  name: 'OverviewEquipment',
                  params: { id: item.id },
                  query: {
                    fromTable: true,
                    ...getParams(),
                  },
                }"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-eye
                </v-icon>
              </v-btn>
            </template>
            {{ $t('overview') }}
          </v-tooltip>
          <v-tooltip bottom v-if="$store.getters.roles == 'provider'">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                @click="
                  () => {
                    itemForCopy = item;
                    copyEquipment(itemForCopy);
                    copyDialog = true;
                  }
                "
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-content-copy
                </v-icon>
              </v-btn>
            </template>
            {{ $t('copy') }}
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                @click="
                  () => {
                    itemToBeDeleted = item;
                    deleteDialog = true;
                  }
                "
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-delete
                </v-icon>
              </v-btn>
            </template>
            {{ $t('delete.message') }}
          </v-tooltip>
        </v-layout>
      </template>
      <template v-slot:item.logo="{ item }" style="width: 7em !important;">
        <v-img
          class="my-2"
          style="border-radius: 8px;"
          height="75"
          width="65"
          :src="item.logo"
          contain
        >
          <template v-slot:placeholder>
            <v-img style="border-radius: 8px;" src="@/assets/img/blur.jpg"></v-img>
          </template>
        </v-img>
      </template>
      <template v-slot:item.details="{ item }">
        <div class="text-bold">
          {{ item.name }}
        </div>
        <div>
          Status: <span class="text-bold">{{ getPublicStatus(item.published) }}</span>
        </div>
        <div>
          Last updated:
          <span class="text-bold">{{ formatDateTimeLocale(item.updatedTimestamp) }}</span>
        </div>
        <div>
          Orderable:
          <span class="text-bold">{{ item.orderable ? 'Orderable' : 'Non Orderable' }}</span>
        </div>
      </template>
      <template v-slot:item.type="{ item }">
        <div>
          <div v-for="(type, index) in item.types.slice(0, 2)" :key="index">
            <v-chip class="my-1" :style="{ backgroundColor: 'rgba(237, 224, 255, 1)' }">
              {{ type }}
            </v-chip>
          </div>
          <v-chip v-if="item.types.length > 2" class="my-1" color="primary200">
            +{{ item.types.length - 2 }} more
          </v-chip>
        </div>
      </template>
      <template v-slot:item.created="{ item }">
        <div>
          {{ formatDateTimeLocale(item.createdTimestamp) }}
        </div>
      </template>
      <template v-slot:item.provider="{ item }">
        <router-link
          :to="{ name: 'OverviewProvider', params: { id: item.provider.id } }"
          style="text-decoration: underline; color: inherit;"
        >
          {{ item.provider.name }}
        </router-link>
      </template>
      <template v-slot:item.hosting="{ item }">
        <router-link
          :to="{ name: 'OverviewHostingInstitution', params: { id: item.hostingInstitution.id } }"
          style="text-decoration: underline; color: inherit;"
        >
          {{ item.hostingInstitution.name }}
        </router-link>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import SearchText from '@/components/shared/search/SearchText';
import EventBus from '@/helpers/event-bus.js';
import util from '@/mixins/util.js';
import InputAutocompleteSearch from '@/components/shared/inputs/InputAutocompleteSearch';

export default {
  name: 'TableEquipment',
  components: { SearchText, InputAutocompleteSearch },
  mixins: [util],
  props: {
    urlApi: String,
    search: Object,
    searchAttributes: Array,
  },
  data: () => ({
    headers: [
      { text: '', value: 'actions', sortable: false },
      { text: 'Logo', value: 'logo', sortable: false },
      { text: 'Details', value: 'details', sortable: true },
      { text: 'Type', value: 'type', sortable: false },
      { text: 'Created', value: 'created', sortable: true },
      { text: 'Provider', value: 'provider', sortable: true },
      { text: 'Hosting', value: 'hosting', sortable: true },
    ],
    items: [],
    totalItems: 0,
    options: {},
    firstLoad: true,
    equipments: [],
    dialogBulkEdit: false,
    bulkEdit: {
      equipments: [],
      equipmentPrototype: {},
      logo: false,
      publicContact: false,
    },
    role: '',
    deleteDialog: false,
    itemToBeDeleted: null,
    copyDialog: false,
    itemForCopy: null,
    newDuplicateItem: null,
  }),
  watch: {
    options: {
      handler() {
        console.log('[Table] Options watch');
        if (!this.firstLoad) {
          this.getDataFromApi().then(data => {
            this.items = data.items;
            this.totalItems = data.total;
          });
        }
      },
      deep: true,
    },
  },
  mounted() {
    console.log('[Table] Mounted');
    if (this.firstLoad) {
      /* Populate table from route */
      this.options.page = this.$route.query.index ? Number(this.$route.query.index) + 1 : 1;
      this.options.itemsPerPage = this.$route.query.size ? Number(this.$route.query.size) : 10;
      this.options.sortBy = [];
      this.options.sortDesc = [];
      if (this.$route.query.sort) {
        const sortList = this.$route.query.sort.split(',');
        for (let i = 0; i < sortList.length; i += 1) {
          this.options.sortBy.push(sortList[i].split(' ')[0]);
          if (sortList[i].split(' ')[1] === 'asc') {
            this.options.sortDesc.push(false);
          } else {
            this.options.sortDesc.push(true);
          }
        }
      }
      if (this.searchAttributes) {
        for (let i = 0; i < this.searchAttributes.length; i += 1) {
          const attr = this.searchAttributes[i];
          if (this.$route.query[attr]) {
            this.$set(this.search, attr, this.$route.query[attr]);
          }
        }
      }
    }
    this.getDataFromApi().then(data => {
      this.items = data.items;
      this.totalItems = data.total;
      this.firstLoad = false;
    });
  },
  methods: {
    searchTextChanged(obj) {
      console.log('[Table] searchTextChanged');
      this.search[obj.value] = obj.search;
      console.log(this.search);
    },
    triggerSearch() {
      console.log('[Table] Search watch');
      this.options.page = 1;
      this.getDataFromApi().then(data => {
        this.items = data.items;
        this.totalItems = data.total;
      });
    },
    triggerClear() {
      console.log('[Table] Clear watch');

      this.search = {};

      this.options.page = 1;
      this.getDataFromApi().then(data => {
        this.items = data.items;
        this.totalItems = data.total;
      });
    },
    getDataFromApi() {
      console.log('[Table] Loading data');
      EventBus.$emit('waiting', true);

      this.role = localStorage.getItem('roles') || '';
      console.log('role:', this.role);

      if (this.role === 'provider') {
        return new Promise(resolve => {
          this.axios
            .get(`${this.urlApi}?providerId=${sessionStorage.getItem('providerId')}`, {
              params: this.getParams(),
            })
            .then(res => {
              let items = [];
              let total = 0;
              if (res.status === 200) {
                items = res.data.content;
                total = res.data.total;
                console.log('Get Equipment', items);
              }
              setTimeout(() => {
                resolve({
                  items,
                  total,
                });
              });
            })
            .finally(() => {
              EventBus.$emit('waiting', false);
            });
        });
      }

      if (this.role === 'super-admin') {
        return new Promise(resolve => {
          this.axios
            .get(`api/v1/equipment/admin`, {
              params: this.getParams(),
            })
            .then(res => {
              let items = [];
              let total = 0;
              if (res.status === 200) {
                items = res.data.content;
                total = res.data.total;
                console.log('Get Equipment', items);
              }
              setTimeout(() => {
                resolve({
                  items,
                  total,
                });
              });
            })
            .finally(() => {
              EventBus.$emit('waiting', false);
            });
        });
      }
    },
    getParams() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      const query = {
        index: page - 1,
        size: itemsPerPage,
      };

      let sort = '';
      for (let i = 0; i < sortBy.length; i += 1) {
        if (i !== 0) {
          sort += ',';
        }
        let dir = 'asc';
        if (sortDesc[i]) {
          dir = 'desc';
        }

        let sortField = sortBy[i];
        if (sortField === 'details') {
          sortField = 'name';
        }
        if (sortField === 'created') {
          sortField = 'createdTimestamp';
        }

        sort += `${sortField} ${dir}`;
      }

      if (sort !== '') {
        query.sort = sort;
      }

      if (this.searchAttributes) {
        for (let i = 0; i < this.searchAttributes.length; i += 1) {
          const attr = this.searchAttributes[i];
          if (this.search[attr]) {
            query[attr] = this.search[attr];
          }
        }
      }

      return query;
    },
    getPublicStatus(published) {
      return published ? 'PUBLIC' : 'PRIVATE';
    },
    triggerBulkEdit() {
      this.dialogBulkEdit = true;
    },
    cancelBulkEdit() {
      this.bulkEdit.equipmentPrototype = '';
      this.bulkEdit.logo = false;
      this.bulkEdit.publicContact = false;
      this.dialogBulkEdit = false;
    },
    submitBulkEdit() {
      EventBus.$emit('waiting', true);
      const equipmentPrototypeId = this.bulkEdit.equipmentPrototype.id;
      const equipmentPrototypeName = this.bulkEdit.equipmentPrototype.name;

      const bodyBulkEdit = {
        logo: this.bulkEdit.logo,
        publicContact: this.bulkEdit.publicContact,
        equipmentPrototype: {
          id: equipmentPrototypeId,
          name: equipmentPrototypeName,
        },
        equipments: this.equipments.map(equipment => ({
          id: equipment.id,
          name: equipment.name,
        })),
      };

      this.axios
        .put('/api/v1/equipment/bulk', bodyBulkEdit)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: 'Equipment Updated Successfully',
          });
          this.dialogBulkEdit = false;
          this.getDataFromApi().then(data => {
            this.items = data.items;
            this.totalItems = data.total;
          });
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    downloadEquipment() {
      this.axios
        .get('api/v1/equipment/admin/download', {
          responseType: 'blob',
        })
        .then(response => {
          const contentDisposition = response.headers['content-disposition'];
          let fileName = 'equipment.xlsx';
          if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename="([^"]*)"/);
            if (fileNameMatch) {
              fileName = fileNameMatch[1];
            }
          }

          const url = window.URL.createObjectURL(new Blob([response.data]));

          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);

          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch(error => {
          console.error('Error downloading file:', error);
        });
    },
    deleteEquipment() {
      if (this.itemToBeDeleted.id) {
        const id = this.itemToBeDeleted.id;

        this.axios
          .delete(`api/v1/equipment/${id}`)
          .then(response => {
            console.log('Equipment deleted successfully:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Deleted Successfully',
            });
            this.deleteDialog = false;
            this.getDataFromApi().then(data => {
              this.items = data.items;
              this.totalItems = data.total;
            });
          })
          .catch(error => {
            console.error('Error deleting equipment:', error);
          });
      }
    },
    copyEquipment() {
      if (this.itemForCopy.id) {
        const id = this.itemForCopy.id;

        this.axios
          .post(`api/v1/equipment/${id}/copy`)
          .then(response => {
            console.log('Equipment copied successfully:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Copied Successfully',
            });
            this.copyDialog = true;
            this.newDuplicateItem = response.data;
          })
          .catch(error => {
            console.error('Error copying equipment:', error);
          })
          .finally(() => {
            this.getDataFromApi().then(data => {
              this.items = data.items;
              this.totalItems = data.total;
            });
          });
      }
    },
  },
};
</script>
